import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Loader2, OctagonX, Play, RefreshCw, ShieldCheck } from 'lucide-react';
import { waysApi, WaysError, type WaysFinding, type WaysSwitch } from '../../services/waysApi';
import WaysPracticePolicy from './WaysPracticePolicy';

/**
 * A project's governance in Ways (PO-92 F5.1 S5.1.3, Ways 0.11): the off switch for agents
 * (collab:moderate), the governor's mode -- shadow, or advisory, which posts each new finding to
 * the project's governance channel (collab:moderate) -- and the governor's findings (audit:read).
 * Ways decides every one of these; a refusal is shown as it comes back, never guessed in advance.
 */

function when(v?: string | number): string {
  if (v === undefined || v === null || v === '') return '';
  const d = typeof v === 'number' ? new Date(v * 1000) : new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

function refusal(e: unknown, need: string): string {
  if (e instanceof WaysError && e.status === 403) return `${e.message} (needs ${need})`;
  return e instanceof Error ? e.message : String(e);
}

export default function WaysGovernance({ projectId, projectName, switches, onSwitchesChanged }: {
  projectId: string;
  projectName: string;
  switches: WaysSwitch[];
  onSwitchesChanged: (s: WaysSwitch[]) => void;
}) {
  const [mode, setMode] = useState<string | null>(null);
  const [findings, setFindings] = useState<WaysFinding[] | null>(null);
  const [findingsError, setFindingsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [actors, setActors] = useState<'agents' | 'all'>('agents');

  const sw = switches.find((s) => s.on && s.scope === 'project' && s.id === projectId);
  const global = switches.find((s) => s.on && s.scope === 'global');

  const load = useCallback(async () => {
    setError(null);
    try { setMode((await waysApi.governanceMode(projectId)).mode); } catch (e) { setError(refusal(e, 'Ways governor')); }
    try {
      setFindings((await waysApi.findings(projectId)).findings);
      setFindingsError(null);
    } catch (e) { setFindings(null); setFindingsError(refusal(e, 'audit:read on this project')); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const flip = async (on: boolean) => {
    if (on && !reason.trim()) { setError('Say why: the reason is shown to everyone the switch stops.'); return; }
    setBusy(true);
    setError(null);
    try {
      const r = await waysApi.setKillswitch({ scope: 'project', id: projectId, on, actors, reason: on ? reason.trim() : (reason.trim() || 'resumed') });
      onSwitchesChanged(r.switches);
      setReason('');
    } catch (e) { setError(refusal(e, 'collab:moderate on this project')); } finally { setBusy(false); }
  };

  const changeMode = async (m: 'shadow' | 'advisory') => {
    setBusy(true);
    setError(null);
    try { setMode((await waysApi.setGovernanceMode(projectId, m)).mode); } catch (e) { setError(refusal(e, 'collab:moderate on this project')); } finally { setBusy(false); }
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <ShieldCheck className="w-4 h-4 text-accent" /> Governance &middot; {projectName}
        </h2>
        <button type="button" onClick={load} title="Refresh" className="p-1 text-gray-400 hover:text-accent">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}

      <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Off switch</h3>
        {global ? (
          <p className="mb-2 text-sm text-red-700 dark:text-red-300">
            A global switch is on ({global.actors === 'all' ? 'everyone' : 'agents'}): {global.reason || 'no reason given'}.
          </p>
        ) : null}
        {sw ? (
          <div className="space-y-2">
            <p className="text-sm text-red-700 dark:text-red-300">
              {sw.actors === 'all' ? 'Everyone is' : 'Agents are'} stopped in this project since {when(sw.at)}: {sw.reason || 'no reason given'}.
            </p>
            <button type="button" disabled={busy} onClick={() => flip(false)}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-emerald-600 text-white text-sm disabled:opacity-50">
              <Play className="w-3.5 h-3.5" /> Resume
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Stopping refuses every write in this project from {actors === 'all' ? 'everyone' : 'agent sessions'} (423), with your reason.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <select value={actors} onChange={(e) => setActors(e.target.value as 'agents' | 'all')}
                className="px-2 py-1 text-sm rounded border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100">
                <option value="agents">Agents only</option>
                <option value="all">Everyone</option>
              </select>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)"
                className="flex-1 min-w-[12rem] px-2 py-1 text-sm rounded border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100" />
              <button type="button" disabled={busy || !reason.trim()} onClick={() => flip(true)}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-red-600 text-white text-sm disabled:opacity-50">
                <OctagonX className="w-3.5 h-3.5" /> Stop {actors === 'all' ? 'everyone' : 'agents'} in this project
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Governor</h3>
        {mode === null ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> : (
          <div className="flex items-center gap-2 text-sm">
            {(['shadow', 'advisory'] as const).map((m) => (
              <button key={m} type="button" disabled={busy || mode === m} onClick={() => changeMode(m)}
                className={clsx('px-3 py-1 rounded-md border',
                  mode === m ? 'border-accent bg-accent/10 text-accent' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300')}>
                {m === 'shadow' ? 'Shadow (record only)' : 'Advisory (post alerts)'}
              </button>
            ))}
          </div>
        )}
        <p className="mt-2 text-xs text-gray-500">
          Advisory posts each new finding once to this project&rsquo;s governance channel, which moderators can read and nobody can post in.
        </p>
      </section>

      <WaysPracticePolicy projectId={projectId} />

      <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Findings</h3>
        {findingsError ? <p className="text-sm text-gray-500">{findingsError}</p>
          : findings === null ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            : !findings.length ? <p className="text-sm text-gray-500">No findings.</p> : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                {findings.map((f, i) => (
                  <li key={f._id || i} className="py-2 text-sm">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className={clsx('px-1.5 rounded', f.severity === 'high' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                        : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300')}>{f.severity}</span>
                      <span className="font-mono text-gray-600 dark:text-gray-300">{f.detector}</span>
                      {f.count ? <span className="text-gray-500">&times;{f.count}</span> : null}
                      <span className="text-gray-400">{when(f.lastSeen)}</span>
                      {f.session ? <span className="font-mono text-gray-400" title={f.session}>s:{f.session.slice(0, 8)}</span> : null}
                    </div>
                    <p className="mt-0.5 text-gray-800 dark:text-gray-100">{f.summary}</p>
                  </li>
                ))}
              </ul>
            )}
      </section>
    </div>
  );
}
